import { useApiUrl, useCustom, useOne } from "@refinedev/core";
import type { AddonDetail, QuizPreparation } from "types/";
import type { AddOnType, MediaProjectResponse } from "pages/media/types";
import type { ProjectAddOnsResponse } from "pages/create-experience/data/addonApi";
import { ANALYTICS_STALE_TIME } from "../../constants/constants";
import { buildVersionFilters } from "../../utils/";
import type { AddonKey } from "../utils/metrics";

const addonsBase = (projectId: string | undefined) =>
  `analytics/projects/${projectId}/analytics/addons`;

/**
 * Fetches one add-on's detail from GET {base}/{addon}/detail, for `version`
 * (undefined: the live one). Errors are silenced: the page shows its own error card.
 */
export const useAddonDetail = (
  projectId: string | undefined,
  addon: AddonKey,
  version: string | undefined,
  enabled: boolean
) =>
  useCustom<AddonDetail>({
    url: `${addonsBase(projectId)}/${addon}/detail`,
    method: "get",
    config: { filters: buildVersionFilters(version) },
    queryOptions: { enabled, staleTime: ANALYTICS_STALE_TIME },
    errorNotification: false,
  });

/**
 * Fetches the quiz preparation from GET {base}/quiz/preparation, for `version`
 * (undefined: the live one). Errors are silenced: the page shows its own error card.
 */
export const useQuizPreparation = (
  projectId: string | undefined,
  version: string | undefined,
  enabled: boolean
) => {
  const query = useCustom<QuizPreparation>({
    url: `${addonsBase(projectId)}/quiz/preparation`,
    method: "get",
    config: { filters: buildVersionFilters(version) },
    queryOptions: { enabled, staleTime: ANALYTICS_STALE_TIME },
    errorNotification: false,
  });
  const preparation = query.data?.data ?? null;
  return {
    preparation,
    preparationLoading: query.isInitialLoading,
    // A failed request must not read as "no data": the panels say so instead.
    preparationFailed: query.isError && !preparation,
  };
};

// The add-ons the Add-ons tab shows (`TAB_ADDONS`): others, e.g. a podcast,
// have no analytics there.
const TAB_ADDON_TYPES: ReadonlySet<AddOnType> = new Set<AddOnType>([
  "Quiz",
  "Summary",
  "Flashcards",
  "Standouts",
]);

// A video media that was generated (its id stays null until then).
const hasVideoMedia = (project: MediaProjectResponse) =>
  project.media.some(
    (media) => media.media_generation_mode === "Video" && !!media.id
  );

export type ProjectAnalyticsTabs = { hasAddons: boolean; hasVideo: boolean };

/**
 * Whether the project has the Add-ons and Video tabs, from the add-ons it
 * enabled: a project nobody has viewed yet still has its add-ons, so the
 * analytics can't tell. Undefined while it loads; on error, the Add-ons tab
 * (it says so) with the Video pill, so the video stays reachable.
 */
export const useProjectAnalyticsTabs = (
  projectId: string | undefined,
  enabled: boolean
): ProjectAnalyticsTabs | undefined => {
  const apiUrl = useApiUrl();
  const { data, isError, isSuccess } = useCustom<ProjectAddOnsResponse>({
    url: `${apiUrl}/media/projects/${projectId}/add-ons`,
    method: "get",
    queryOptions: {
      enabled: enabled && !!projectId,
      staleTime: ANALYTICS_STALE_TIME,
    },
    errorNotification: false,
  });
  const enabledAddOns = data?.data.enabled?.enabled_add_ons;
  // Older projects may have no add-ons list: read the video from the
  // project's media instead (the analytics pages already load the project).
  const project = useOne<MediaProjectResponse>({
    resource: "media/projects",
    id: projectId,
    queryOptions: {
      enabled: enabled && !!projectId && isSuccess && !enabledAddOns,
    },
    errorNotification: false,
  });
  if (isError) return { hasAddons: true, hasVideo: true };
  if (!isSuccess) return undefined;
  if (!enabledAddOns) {
    // Can't tell: keep the video reachable.
    if (project.isError) return { hasAddons: false, hasVideo: true };
    if (!project.data) return undefined;
    return { hasAddons: false, hasVideo: hasVideoMedia(project.data.data) };
  }
  return {
    hasAddons: enabledAddOns.some((type) => TAB_ADDON_TYPES.has(type)),
    hasVideo: enabledAddOns.includes("Video"),
  };
};
